import React, { useState, useRef, useCallback } from 'react';
import { t } from '../tokens';

interface FeaturesSheetProps {
  onClose: () => void;
}

const gold      = t.gold;
const beige     = t.beige;
const darkBrown = t.darkBrown;
const medBrown  = t.medBrown;

// ── Reusable image background block ─────────────────────────────────────────
const BgImage = ({ src, alt, children }: { src: string; alt: string; children?: React.ReactNode }) => (
  <div style={{ position: 'relative', borderRadius: '12px', overflow: 'hidden', height: '180px', marginBottom: '16px', flexShrink: 0 }}>
    <img src={src} alt={alt} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }} />
    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(35,30,27,0.15) 0%, rgba(39,27,19,0.75) 100%)' }} />
    {children && (
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {children}
      </div>
    )}
  </div>
);

const slides = [
  {
    title: 'Welcome to Chord Notator',
    subtitle: 'Your professional chord chart companion for all music',
    content: (
      <div style={{ textAlign: 'center' }}>
        <p style={{ fontSize: '15px', color: beige, lineHeight: '1.6', marginBottom: '28px' }}>
          Create, edit, and perform with professional chord charts.
        </p>
        {/* All Device Access — emphasized */}
        <div style={{ background: 'rgba(39,27,19,0.6)', border: `1px solid rgba(212,168,83,0.25)`, borderRadius: '14px', padding: '20px 16px' }}>
          <p style={{ fontFamily: 'Kumbh Sans, sans-serif', fontWeight: 500, fontSize: '13px', letterSpacing: '0.1em', textTransform: 'uppercase' as const, color: gold, marginBottom: '20px', opacity: 0.9 }}>
            All Device Access — Anytime
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '32px', alignItems: 'flex-end' }}>
            {/* Smartphone */}
            <div style={{ display: 'flex', flexDirection: 'column' as const, alignItems: 'center', gap: '8px' }}>
              <svg width="28" height="44" viewBox="0 0 24 24" fill="none" stroke={gold} strokeWidth="1.5">
                <rect x="7" y="2" width="10" height="20" rx="2" />
                <circle cx="12" cy="18" r="0.5" fill={gold} />
              </svg>
              <span style={{ fontSize: '11px', color: beige, opacity: 0.7, letterSpacing: '0.05em' }}>Mobile</span>
            </div>
            {/* Tablet */}
            <div style={{ display: 'flex', flexDirection: 'column' as const, alignItems: 'center', gap: '8px' }}>
              <svg width="38" height="44" viewBox="0 0 24 24" fill="none" stroke={gold} strokeWidth="1.5">
                <rect x="4" y="3" width="16" height="18" rx="2" />
                <circle cx="12" cy="17.5" r="0.5" fill={gold} />
              </svg>
              <span style={{ fontSize: '11px', color: beige, opacity: 0.7, letterSpacing: '0.05em' }}>Tablet</span>
            </div>
            {/* Desktop */}
            <div style={{ display: 'flex', flexDirection: 'column' as const, alignItems: 'center', gap: '8px' }}>
              <svg width="50" height="44" viewBox="0 0 24 24" fill="none" stroke={gold} strokeWidth="1.5">
                <rect x="2" y="3" width="20" height="14" rx="2" />
                <line x1="8" y1="21" x2="16" y2="21" strokeLinecap="round" />
                <line x1="12" y1="17" x2="12" y2="21" />
              </svg>
              <span style={{ fontSize: '11px', color: beige, opacity: 0.7, letterSpacing: '0.05em' }}>Desktop</span>
            </div>
          </div>
        </div>
      </div>
    ),
  },
  {
    title: 'Create Beautiful Charts',
    subtitle: 'Professional chord notation made simple',
    content: (
      <div>
        <BgImage src="/hero-laptop.png" alt="Chord Notator editor on MacBook" />
        <p style={{ fontSize: '15px', color: beige, lineHeight: '1.6', textAlign: 'center' }}>
          Build chord charts with the full-featured editor. Chord palette, barlines, repeats, annotations — everything you need.
        </p>
      </div>
    ),
  },
  {
    title: 'MIDI Integration',
    subtitle: 'Connect your keyboard and play chords directly',
    content: (
      <div>
        <BgImage src="/midi-connection.jpg" alt="MIDI keyboard connected to Chord Notator" />
        <p style={{ fontSize: '15px', color: beige, lineHeight: '1.6', textAlign: 'center' }}>
          Connect any MIDI keyboard and play chords to add them instantly to your chart. Perfect for quick composition and live input.
        </p>
      </div>
    ),
  },
  {
    title: 'Jam Sessions',
    subtitle: 'Collaborate with musicians in real-time',
    content: (
      <div>
        <BgImage src="/JamSession.jpg" alt="Musicians jamming with Chord Notator">
          {/* Jam badge overlay — icon from Figma ICON SET (path extracted from MainButtonJamSession.tsx) */}
          <div style={{ background: 'rgba(39,27,19,0.85)', borderRadius: '10px', padding: '10px 18px', display: 'flex', alignItems: 'center', gap: '10px', border: `1px solid rgba(212,168,83,0.4)` }}>
            {/* Figma Jam icon — connected-nodes share path, 14×16 viewBox */}
            <svg width="26" height="30" viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9.40527 0C11.031 0 12.3486 1.37032 12.3486 3.06055C12.3486 4.75072 11.031 6.12109 9.40527 6.12109C8.7409 6.12106 8.1303 5.88894 7.6377 5.50293C6.8291 5.89529 6.05247 6.56926 5.40039 7.94531C5.70734 8.42843 5.88765 9.00658 5.8877 9.62891C5.8877 9.69213 5.88449 9.75511 5.88086 9.81738C6.64315 10.4394 7.52265 10.8592 8.54004 11.1064C9.05604 10.2218 9.98881 9.62891 11.0566 9.62891C12.6822 9.62908 13.9999 10.9994 14 12.6895C14 14.3796 12.6822 15.7498 11.0566 15.75C9.4309 15.75 8.1123 14.3797 8.1123 12.6895C8.11231 12.5523 8.1238 12.4175 8.14062 12.2852C7.4378 11.8253 6.55886 11.4652 5.39551 11.3213C4.8681 12.1457 3.96708 12.6895 2.94336 12.6895C1.31776 12.6893 0 11.319 0 9.62891C0.000130364 7.9389 1.31784 6.56853 2.94336 6.56836C3.28051 6.56836 3.60361 6.63077 3.90527 6.73926C5.13842 6.29114 6.00851 5.53152 6.74707 4.36914C6.56591 3.97206 6.46193 3.52924 6.46191 3.06055C6.46191 1.37037 7.7796 8.84115e-05 9.40527 0Z" fill={gold}/>
            </svg>
            <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
              <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: gold }} />
              <span style={{ color: gold, fontSize: '12px', fontWeight: 600, letterSpacing: '0.05em' }}>JAM SESSION ACTIVE</span>
            </div>
          </div>
        </BgImage>
        <p style={{ fontSize: '15px', color: beige, lineHeight: '1.6', textAlign: 'center' }}>
          Start a jam session and invite musicians. Share chord charts in real-time, from anywhere in the world.
        </p>
      </div>
    ),
  },
  {
    title: 'Perform Live',
    subtitle: 'Gig mode for seamless live performance',
    content: (
      <div>
        <BgImage src="/live-performance.jpg" alt="Musician performing live with Chord Notator on tablet" />
        <p style={{ fontSize: '15px', color: beige, lineHeight: '1.6', textAlign: 'center' }}>
          Switch to Gig mode for distraction-free performance. Navigate set lists and focus entirely on your music.
        </p>
      </div>
    ),
  },
  {
    title: 'Ready to Start?',
    subtitle: 'Begin creating your first chord chart',
    content: (
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '56px', marginBottom: '16px' }}>🎵</div>
        <p style={{ fontSize: '15px', color: beige, lineHeight: '1.6', marginBottom: '12px' }}>
          You're all set! Start creating professional chord charts for any genre — jazz, rock, pop, worship, and more.
        </p>
        <p style={{ fontSize: '14px', color: gold, lineHeight: '1.6' }}>
          Tap the ✕ to close and get started.
        </p>
      </div>
    ),
  },
];

const FeaturesSheet: React.FC<FeaturesSheetProps> = ({ onClose }) => {
  const [current, setCurrent] = useState(0);
  const total = slides.length;

  // ── Swipe-to-close drag handling ──────────────────────────────────────────
  const sheetRef = useRef<HTMLDivElement>(null);
  const dragStartY = useRef<number | null>(null);
  const dragDeltaY = useRef(0);
  const dragStartTime = useRef<number>(0);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    dragStartY.current = e.clientY;
    dragStartTime.current = Date.now();
    dragDeltaY.current = 0;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (dragStartY.current === null) return;
    const delta = e.clientY - dragStartY.current;
    if (delta < 0) return; // only downward drag
    dragDeltaY.current = delta;
    if (sheetRef.current) {
      sheetRef.current.style.transform = `translateY(${delta}px)`;
      sheetRef.current.style.transition = 'none';
    }
  }, []);

  const onPointerUp = useCallback(() => {
    if (dragStartY.current === null) return;
    const elapsed = Date.now() - dragStartTime.current;
    const velocity = Math.abs(dragDeltaY.current) / elapsed;
    if (dragDeltaY.current > 120 || velocity > 0.4) {
      onClose();
    } else {
      if (sheetRef.current) {
        sheetRef.current.style.transform = 'translateY(0)';
        sheetRef.current.style.transition = 'transform 300ms cubic-bezier(0.32, 0.72, 0, 1)';
      }
    }
    dragStartY.current = null;
  }, [onClose]);

  const slide = slides[current];

  return (
    /* Backdrop */
    <div
      className="features-sheet-backdrop"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        zIndex: 50,
        display: 'flex',
        alignItems: 'flex-end',
      }}
    >
      {/* Sheet */}
      <div
        ref={sheetRef}
        className="features-sheet"
        onClick={e => e.stopPropagation()}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        style={{
          width: '100%',
          maxHeight: '85dvh',
          backgroundColor: darkBrown,
          borderRadius: '20px 20px 0 0',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          touchAction: 'none',
        }}
      >
        {/* Drag handle */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 4px' }}>
          <div style={{ width: '36px', height: '4px', borderRadius: '2px', backgroundColor: medBrown }} />
        </div>

        {/* Progress + close */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 20px 4px' }}>
          <span style={{ fontSize: '12px', color: gold, fontFamily: 'Kumbh Sans, sans-serif', letterSpacing: '0.05em' }}>
            {current + 1} / {total}
          </span>
          <div style={{ flex: 1, margin: '0 12px', height: '2px', backgroundColor: medBrown, borderRadius: '1px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${((current + 1) / total) * 100}%`, backgroundColor: gold, borderRadius: '1px', transition: 'width 300ms cubic-bezier(0.23, 1, 0.32, 1)' }} />
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: beige, fontSize: '20px', cursor: 'pointer', lineHeight: 1, padding: '4px', opacity: 0.7 }}
            aria-label="Fermer"
          >
            ✕
          </button>
        </div>

        {/* Slide content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ fontFamily: 'Kumbh Sans, sans-serif', fontWeight: 500, fontSize: '22px', color: gold, marginBottom: '6px', lineHeight: 1.2 }}>
              {slide.title}
            </h2>
            <p style={{ fontFamily: 'Kumbh Sans, sans-serif', fontWeight: 400, fontSize: '14px', color: beige, opacity: 0.75, marginBottom: '20px', lineHeight: 1.5 }}>
              {slide.subtitle}
            </p>
          </div>
          {slide.content}
        </div>

        {/* Navigation */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px 28px' }}>
          <button
            onClick={() => current > 0 && setCurrent(c => c - 1)}
            style={{
              background: 'none', border: 'none', cursor: current > 0 ? 'pointer' : 'default',
              fontFamily: 'Kumbh Sans, sans-serif', fontSize: '14px',
              color: current > 0 ? beige : 'transparent', letterSpacing: '0.04em',
              transition: 'opacity 160ms',
            }}
          >
            ← PREV
          </button>

          {/* Dots */}
          <div style={{ display: 'flex', gap: '6px' }}>
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                style={{
                  width: i === current ? '18px' : '6px',
                  height: '6px',
                  borderRadius: '3px',
                  backgroundColor: i === current ? gold : medBrown,
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 250ms cubic-bezier(0.23, 1, 0.32, 1)',
                  padding: 0,
                }}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>

          <button
            onClick={() => current < total - 1 ? setCurrent(c => c + 1) : onClose()}
            style={{
              background: current === total - 1 ? gold : medBrown,
              border: 'none', cursor: 'pointer',
              fontFamily: 'Kumbh Sans, sans-serif', fontSize: '14px',
              color: current === total - 1 ? darkBrown : beige,
              fontWeight: current === total - 1 ? 600 : 400,
              letterSpacing: '0.04em',
              borderRadius: '20px', padding: '8px 20px',
              transition: 'all 200ms cubic-bezier(0.23, 1, 0.32, 1)',
            }}
          >
            {current < total - 1 ? 'NEXT →' : 'CLOSE'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeaturesSheet;
